'use client'

import { useState, useRef, useEffect, useId } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/navigation'
import { Button } from '@/General/Button'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useStore } from '@/store'
import { handleError } from './utils/errorHandling'
import { CheckIcon } from '@heroicons/react/24/outline'
import {
  useCreateCustomerMutation,
  useAddCustomerToSegmentMutation,
  useCustomerSegmentsQuery,
  type CustomerIoWorkspace,
} from '@/generated/graphql'
import { EMAIL_REGEX } from '@/validation'
import { tracking } from '@/tracking'
import { useCaptcha } from './useCaptcha'
import { EmailSuggestionModal } from '../../app/account/update/controls/EmailUpdate/EmailSuggestionModal'
import type {
  AdvertisingModalFormData,
  AdvertisingModalContent,
  AdvertisingModalConfig,
  AdvertisingModalEventCallbacks,
} from './types'

export interface AdvertisingModalEmbeddedProps {
  content: AdvertisingModalContent
  /** Event name fired on success (must match a key in `eventRegistry` when using shared stats). */
  trackingEventName: string
  /** Merged into `tracking.track` payload (e.g. `Free download name`, `source`). */
  trackingProperties?: Record<string, unknown>
  config?: Omit<
    AdvertisingModalConfig,
    'showCloseButton' | 'closeOnBackdropClick' | 'maxWidth'
  >
  callbacks?: AdvertisingModalEventCallbacks
  className?: string
  customInputClassName?: string
  customButtonClassName?: string
  customTitleClassName?: string
  customIntroClassName?: string
  customSuccessClassName?: string
  customSuccessTitleClassName?: string
  customSuccessMessageClassName?: string
  existingAccountSuccessTitle?: string
  existingAccountSuccessMessage?: string
  hideTitleInSuccess?: boolean
  hideLabels?: boolean
  hideErrorBox?: boolean
  /** Overrides padding on the inner wrapper when `layout` is `stacked` (image + form column). */
  stackedContentClassName?: string
  /** Class on the `<form>` (default `space-y-6`). */
  formClassName?: string
  /** Passed to `createCustomer` (default 1). */
  howSignedUp?: number
  /**
   * After a successful signup, navigate here (e.g. login with redirect to claim a free product).
   * `grantFreeDownload` requires an authenticated session (`SameUser` policy).
   */
  successRedirectUrl?: string
  /**
   * CustomerIO action(s) to fire after a successful signup.
   * `segmentId` adds the new customer to a CIO manual segment.
   * `transactionalEmailId` will be wired up once the backend resolver is deployed.
   */
  customerIo?: {
    segmentId?: number
    segmentIds?: number[]
    includeDefaultNewsletterSegmentsWhenMissing?: boolean
    workspace?: CustomerIoWorkspace
  }
}

const MAIN_NEWSLETTER_SEGMENT_IDS = [10, 16]

/**
 * Embedded version of AdvertisingModal that renders inline on a page.
 * Handles: bot verification, email typo suggestions, existing-account detection.
 */
export const AdvertisingModalEmbedded = ({
  content,
  trackingEventName,
  trackingProperties,
  config = {},
  callbacks = {},
  className = '',
  customInputClassName,
  customButtonClassName,
  customTitleClassName,
  customIntroClassName,
  customSuccessClassName,
  customSuccessTitleClassName,
  customSuccessMessageClassName,
  existingAccountSuccessTitle = 'Check your email',
  existingAccountSuccessMessage = "We've emailed you details on how to get your download. Please check your inbox.",
  hideTitleInSuccess = false,
  hideLabels = false,
  hideErrorBox = false,
  stackedContentClassName,
  formClassName = 'space-y-6',
  howSignedUp: howSignedUpProp = 1,
  successRedirectUrl,
  customerIo,
}: AdvertisingModalEmbeddedProps) => {
  const router = useRouter()
  const user = useStore(state => state.user)
  const formIdPrefix = useId()

  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [existingAccount, setExistingAccount] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [showEmailSuggestion, setShowEmailSuggestion] = useState(false)
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null)
  const [suggestionText, setSuggestionText] = useState('')
  const [originalEmailForModal, setOriginalEmailForModal] = useState('')
  const hasAcceptedSuggestionRef = useRef(false)
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current)
    }
  }, [])

  const {
    layout = 'side-by-side',
    hideForLoggedInUsers = false,
    showFormForLoggedInUsers = true,
    enableCaptcha = false,
  } = config

  const { verifyCaptcha } = useCaptcha({ enabled: enableCaptcha })
  const { mutateAsync: createCustomerAsync } = useCreateCustomerMutation()
  const { mutateAsync: addToSegmentAsync } = useAddCustomerToSegmentMutation()
  const configuredSegmentIds = customerIo?.segmentIds?.length
    ? customerIo.segmentIds
    : customerIo?.segmentId
      ? [customerIo.segmentId]
      : []
  const targetSegmentIds = configuredSegmentIds.length
    ? configuredSegmentIds
    : MAIN_NEWSLETTER_SEGMENT_IDS
  const {
    data: customerSegmentsData,
    isLoading: customerSegmentsLoading,
    isError: customerSegmentsError,
  } = useCustomerSegmentsQuery(
    { workspace: customerIo?.workspace },
    {
      enabled: Boolean(user),
      staleTime: 60_000,
    }
  )
  const customerSegmentIds = new Set(
    customerSegmentsData?.customerSegments?.segments
      ?.map(segment => segment?.id)
      .filter((id): id is number => typeof id === 'number') ?? []
  )
  const isSubscribedToTarget =
    Boolean(user) &&
    customerSegmentsData?.customerSegments != null &&
    targetSegmentIds.some(segmentId => customerSegmentIds.has(segmentId))
  const isSubscribedToMainNewsletter = MAIN_NEWSLETTER_SEGMENT_IDS.every(
    segmentId => customerSegmentIds.has(segmentId)
  )
  const segmentIdsToAdd = [
    ...targetSegmentIds,
    ...(user &&
    customerIo?.includeDefaultNewsletterSegmentsWhenMissing &&
    !isSubscribedToMainNewsletter
      ? MAIN_NEWSLETTER_SEGMENT_IDS
      : []),
  ].filter(
    (segmentId, index, segmentIds) => segmentIds.indexOf(segmentId) === index
  )

  const {
    title = 'Special Offer',
    introHtml,
    imageUrl,
    imageAlt = 'Promotional image',
    buttonText = 'Get Started',
    submitButtonText = 'Submit',
    successMessage = 'Thank you! Check your email for your download.',
    formFields = [
      {
        name: 'name',
        label: 'Name',
        type: 'text' as const,
        required: true,
        validation: {
          pattern: /^[a-zA-Z]+\s+[a-zA-Z]+/,
          message: 'Please enter your first and last name',
        },
      },
      {
        name: 'email',
        label: 'Email Address',
        type: 'email' as const,
        required: true,
        validation: {
          pattern: EMAIL_REGEX,
          message: 'Please enter a valid email address',
        },
      },
    ],
    additionalFormFields = [],
  } = content

  const shouldHideModal = hideForLoggedInUsers && !!user
  const shouldShowForm = !user || showFormForLoggedInUsers
  const allFormFields = [...formFields, ...additionalFormFields]

  // Pre-populate form with logged-in user data
  const defaultValues: Record<string, string> = {}
  if (user) {
    allFormFields.forEach(field => {
      if (field.name === 'email' && user.email) {
        defaultValues.email = user.email
      } else if (field.name === 'name' && user.firstName) {
        const lastName = user.lastName || ''
        const fullName = `${user.firstName} ${lastName}`.trim()
        defaultValues.name =
          fullName.length > user.firstName.length ? fullName : user.firstName
      }
    })
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<AdvertisingModalFormData>({ defaultValues })

  const onCreateSuccess = (
    email: string,
    firstName: string,
    lastName: string,
    customerId: number | null | undefined,
    formData: AdvertisingModalFormData
  ) => {
    setIsSuccess(true)
    tracking.identify(email, {
      customerId: customerId ?? undefined,
      firstName,
      lastName,
    })
    tracking.track(trackingEventName, {
      offer: content.title,
      addToNewsletter: true,
      ...(trackingProperties ?? {}),
    })
    callbacks?.onFormSuccess?.(formData)
    reset()
    if (successRedirectUrl) {
      redirectTimeoutRef.current = setTimeout(() => {
        router.push(successRedirectUrl)
      }, 1800)
    }
  }

  const handleFormSubmit = async (data: AdvertisingModalFormData) => {
    setIsLoading(true)
    setServerError(null)
    callbacks?.onFormSubmit?.(data)

    if (enableCaptcha) {
      try {
        await verifyCaptcha()
      } catch {
        setServerError('Verification failed. Please try again.')
        setIsLoading(false)
        return
      }
    }

    const nameParts = (data.name ?? '').split(' ')
    const firstName = nameParts[0] ?? ''
    const lastName = nameParts.slice(1).join(' ')

    try {
      if (user && segmentIdsToAdd.length > 0) {
        await Promise.all(
          segmentIdsToAdd.map(segmentId =>
            addToSegmentAsync({
              segmentId,
              workspace: customerIo?.workspace,
            })
          )
        )
        onCreateSuccess(
          data.email,
          firstName || user.firstName || '',
          lastName || user.lastName || '',
          user.id,
          data
        )
        return
      }

      const result = await createCustomerAsync({
        firstName,
        lastName,
        email: data.email,
        howSignedUp: howSignedUpProp,
        addToNewsletter: true,
        skipEmailValidation: hasAcceptedSuggestionRef.current,
      })

      if (result.createCustomer?.success) {
        const customerId = result.createCustomer.customerId

        if (configuredSegmentIds.length > 0 && customerId) {
          try {
            await Promise.all(
              configuredSegmentIds.map(segmentId =>
                addToSegmentAsync({
                  segmentId,
                  workspace: customerIo?.workspace,
                })
              )
            )
          } catch {
            // Non-fatal — signup already succeeded
          }
        }

        onCreateSuccess(data.email, firstName, lastName, customerId, data)
        return
      }

      const msg = result.createCustomer?.message ?? ''
      const ev = result.createCustomer?.emailValidation

      // Email suggestion — show modal on first attempt only
      if (
        !hasAcceptedSuggestionRef.current &&
        ev?.suggestion &&
        ev?.correctedEmail
      ) {
        setSuggestedEmail(ev.correctedEmail)
        setSuggestionText(ev.suggestion)
        setOriginalEmailForModal(data.email)
        setShowEmailSuggestion(true)
        return
      }

      // Post-suggestion: server still flagging email — force-skip validation
      if (hasAcceptedSuggestionRef.current && ev?.suggestion) {
        const retry = await createCustomerAsync({
          firstName,
          lastName,
          email: data.email,
          howSignedUp: howSignedUpProp,
          addToNewsletter: true,
          skipEmailValidation: true,
        })

        if (retry.createCustomer?.success) {
          const customerId = retry.createCustomer.customerId

          if (configuredSegmentIds.length > 0 && customerId) {
            try {
              await Promise.all(
                configuredSegmentIds.map(segmentId =>
                  addToSegmentAsync({
                    segmentId,
                    workspace: customerIo?.workspace,
                  })
                )
              )
            } catch {
              // Non-fatal — signup already succeeded
            }
          }

          onCreateSuccess(data.email, firstName, lastName, customerId, data)
          return
        }

        const retryMsg = retry.createCustomer?.message ?? ''
        if (
          retryMsg.toLowerCase().includes('already exists') ||
          retryMsg.toLowerCase().includes('already registered')
        ) {
          setExistingAccount(true)
          setIsSuccess(true)
          return
        }

        setServerError(
          retry.createCustomer?.emailValidation?.errorMessage ||
            retryMsg ||
            'Could not complete signup. Please try again.'
        )
        return
      }

      // Existing account — show the campaign-specific fallback copy.
      if (
        msg.toLowerCase().includes('already exists') ||
        msg.toLowerCase().includes('already registered')
      ) {
        setExistingAccount(true)
        setIsSuccess(true)
        return
      }

      // Generic failure
      setServerError(msg || 'An error occurred. Please try again.')
    } catch (error) {
      const errMsg = handleError(
        error,
        callbacks?.onFormError,
        'An error occurred. Please try again.'
      )
      setServerError(errMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptSuggestion = (acceptedEmail: string) => {
    hasAcceptedSuggestionRef.current = true
    setValue('email', acceptedEmail)
    setShowEmailSuggestion(false)
    setSuggestedEmail(null)
    setSuggestionText('')
    setOriginalEmailForModal('')
    requestAnimationFrame(() => {
      void handleSubmit(handleFormSubmit)()
    })
  }

  const handleDeclineSuggestion = () => {
    hasAcceptedSuggestionRef.current = true
    setShowEmailSuggestion(false)
    setSuggestedEmail(null)
    setSuggestionText('')
    setOriginalEmailForModal('')
    requestAnimationFrame(() => {
      void handleSubmit(handleFormSubmit)()
    })
  }

  const handleButtonOnlyClick = async () => {
    callbacks?.onButtonClick?.()

    if (!user || targetSegmentIds.length === 0) return

    setIsLoading(true)
    setServerError(null)

    try {
      await Promise.all(
        segmentIdsToAdd.map(segmentId =>
          addToSegmentAsync({
            segmentId,
            workspace: customerIo?.workspace,
          })
        )
      )

      const email = user.email ?? ''
      const firstName = user.firstName ?? ''
      const lastName = user.lastName ?? ''

      onCreateSuccess(email, firstName, lastName, user.id, {
        email,
        name: `${firstName} ${lastName}`.trim(),
      })
    } catch (error) {
      const errMsg = handleError(
        error,
        callbacks?.onFormError,
        'Could not complete signup. Please try again.'
      )
      setServerError(errMsg)
    } finally {
      setIsLoading(false)
    }
  }

  if (shouldHideModal) return null
  if (isSubscribedToTarget) return null
  if (user && (customerSegmentsLoading || customerSegmentsError)) return null

  const validationErrors = Object.values(errors)
    .map(error => error?.message)
    .filter((message): message is string => Boolean(message))

  const allErrors = serverError
    ? [serverError, ...validationErrors]
    : validationErrors

  const renderImage = () => {
    if (!imageUrl) return null
    return (
      <div
        className={`relative ${layout === 'stacked' ? 'w-full h-48 md:h-56' : 'w-full md:w-1/2 self-stretch min-h-[160px] md:min-h-[300px]'}`}
      >
        <img
          src={imageUrl}
          alt={imageAlt}
          className={`w-full h-full object-cover ${layout === 'stacked' ? 'rounded-t-lg' : 'rounded-lg'}`}
        />
      </div>
    )
  }

  const renderContent = () => {
    if (isSuccess) {
      const successTitle = existingAccount
        ? existingAccountSuccessTitle
        : 'Success!'
      const successBody = existingAccount
        ? existingAccountSuccessMessage
        : successMessage
      return (
        <div className={customSuccessClassName || 'text-center py-12'}>
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
          </div>
          <h3
            className={
              customSuccessTitleClassName ||
              'text-2xl font-semibold text-gray-900 mb-3'
            }
          >
            {successTitle}
          </h3>
          <p
            className={customSuccessMessageClassName || 'text-lg text-gray-600'}
          >
            {successBody}
          </p>
        </div>
      )
    }

    if (!shouldShowForm) {
      return (
        <div className="space-y-6">
          {introHtml && (
            <div
              className={
                customIntroClassName || 'text-gray-700 leading-relaxed'
              }
              dangerouslySetInnerHTML={{ __html: introHtml }}
            />
          )}
          <Button
            onClick={handleButtonOnlyClick}
            color="green"
            size="lg"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Submitting...' : buttonText}
          </Button>
        </div>
      )
    }

    return (
      <form
        onSubmit={e => void handleSubmit(handleFormSubmit)(e)}
        noValidate
        className={formClassName}
      >
        {introHtml && (
          <div
            className={customIntroClassName || 'text-gray-700 leading-relaxed'}
            dangerouslySetInnerHTML={{ __html: introHtml }}
          />
        )}

        {!hideErrorBox && <ErrorBox errors={allErrors} />}

        <div className="space-y-4">
          {allFormFields.map(field => {
            const fieldError = errors[field.name]
            const isTextarea = field.type === 'textarea'
            const fieldId = `${formIdPrefix}-${field.name}`
            const errorId = `${fieldId}-error`

            return (
              <div key={field.name}>
                {!hideLabels && field.label && (
                  <label
                    htmlFor={fieldId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {field.label}
                    {field.required && (
                      <span className="text-red-600 ml-1">*</span>
                    )}
                  </label>
                )}

                {isTextarea ? (
                  <textarea
                    {...register(field.name, {
                      required: field.required
                        ? `${field.label} is required`
                        : false,
                      pattern: field.validation?.pattern
                        ? {
                            value: field.validation.pattern,
                            message:
                              field.validation.message || 'Invalid format',
                          }
                        : undefined,
                      minLength: field.validation?.minLength
                        ? {
                            value: field.validation.minLength,
                            message: `Minimum ${field.validation.minLength} characters required`,
                          }
                        : undefined,
                      maxLength: field.validation?.maxLength
                        ? {
                            value: field.validation.maxLength,
                            message: `Maximum ${field.validation.maxLength} characters allowed`,
                          }
                        : undefined,
                    })}
                    id={fieldId}
                    placeholder={field.placeholder}
                    rows={4}
                    aria-invalid={!!fieldError}
                    aria-describedby={fieldError ? errorId : undefined}
                    className={
                      customInputClassName ||
                      `block w-full rounded border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 text-base ${
                        fieldError
                          ? 'ring-red-500 focus:ring-red-500 focus:ring-2'
                          : 'ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-500 hover:ring-gray-400'
                      }`
                    }
                  />
                ) : (
                  <input
                    {...register(field.name, {
                      required: field.required
                        ? `${field.label} is required`
                        : false,
                      pattern: field.validation?.pattern
                        ? {
                            value: field.validation.pattern,
                            message:
                              field.validation.message || 'Invalid format',
                          }
                        : undefined,
                      minLength: field.validation?.minLength
                        ? {
                            value: field.validation.minLength,
                            message: `Minimum ${field.validation.minLength} characters required`,
                          }
                        : undefined,
                      maxLength: field.validation?.maxLength
                        ? {
                            value: field.validation.maxLength,
                            message: `Maximum ${field.validation.maxLength} characters allowed`,
                          }
                        : undefined,
                    })}
                    type={field.type || 'text'}
                    id={fieldId}
                    placeholder={field.placeholder}
                    aria-invalid={!!fieldError}
                    aria-describedby={fieldError ? errorId : undefined}
                    className={
                      customInputClassName ||
                      `block w-full rounded border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 text-base ${
                        fieldError
                          ? 'ring-red-500 focus:ring-red-500 focus:ring-2'
                          : 'ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-500 hover:ring-gray-400'
                      }`
                    }
                  />
                )}

                {fieldError && (
                  <p
                    id={errorId}
                    className="text-red-600 text-sm mt-1"
                    role="alert"
                  >
                    {fieldError.message}
                  </p>
                )}
              </div>
            )
          })}
        </div>

        <Button
          type="submit"
          color="green"
          disabled={isLoading}
          className={customButtonClassName || 'w-full'}
        >
          {isLoading ? 'Submitting...' : submitButtonText}
        </Button>
      </form>
    )
  }

  const containerClass =
    layout === 'side-by-side'
      ? 'flex flex-col md:flex-row gap-8 items-stretch'
      : 'flex flex-col gap-6'

  const contentWrapperClass =
    layout === 'side-by-side' && imageUrl
      ? 'w-full md:w-1/2 flex flex-col justify-center'
      : 'w-full'

  return (
    <>
      <EmailSuggestionModal
        isOpen={showEmailSuggestion && !!suggestedEmail}
        onClose={handleDeclineSuggestion}
        suggestion={suggestionText}
        originalEmail={originalEmailForModal}
        correctedEmail={suggestedEmail || undefined}
        onAcceptSuggestion={handleAcceptSuggestion}
        onDecline={handleDeclineSuggestion}
      />
      <div
        className={`bg-white border rounded-lg shadow-lg mb-8 ${layout === 'stacked' ? 'overflow-hidden' : 'p-4'} ${className}`}
      >
        <div className={containerClass}>
          {layout === 'side-by-side' ? (
            <>
              {renderImage()}
              <div className={contentWrapperClass}>
                {title && !(isSuccess && hideTitleInSuccess) && (
                  <h2
                    className={
                      customTitleClassName ||
                      'text-2xl font-bold text-gray-900 mb-4'
                    }
                  >
                    {title}
                  </h2>
                )}
                {renderContent()}
              </div>
            </>
          ) : (
            <>
              {renderImage()}
              <div
                className={stackedContentClassName ?? 'px-6 pt-6 pb-8 md:px-8'}
              >
                {title && !(isSuccess && hideTitleInSuccess) && (
                  <h2
                    className={
                      customTitleClassName ||
                      'text-2xl font-bold text-gray-900 mb-6'
                    }
                  >
                    {title}
                  </h2>
                )}
                {renderContent()}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
